import React from 'react';
import { connect } from 'react-redux';
import { Redirect, RouteComponentProps, StaticContext, withRouter } from 'react-router';

import { selectors as accountListSelectors } from '../../../slices/accounts/list';
import { selectors as authSelectors } from '../../../slices/auth';
import { getAccountUsageForUser, IAccountUsagePayload } from '../../../slices/auth/getAccountUsageForUser.thunks';
import { RootState } from '../../../slices/rootReducer';
import { AccessTypes } from '../../common/domain/accountAccessType';
import { EnterpriseSubscription } from './enterprise';
import { LiteSubscription } from './lite';
import { ProfessionalSubscription } from './professional';
import { fetchAccountsList } from '../../../slices/accounts/list.thunks';
import { ListParams } from '../../../slices/utils';
import { FilterOperators, Ifilter } from '../../common/filters/interfaces';
import { BlockedByFeature, BlockedByFeatureType, notAvailablePropExceptIn } from '../../../featureToggles/features/utils';
import { Features, isFeatureEnabled } from '../../../featureToggles/features';
import { UserTypes } from '../../common/domain/userTypes';
import { IAccount } from '../../../slices/accounts/account';

const isTrialOfTiersEnabled = isFeatureEnabled(Features.TrialsOfTiers);

interface StateProps {
  accessType?: AccessTypes;
  cannotAccessThisPage?: boolean;
  accountId?: string;
  // use for Features.TrialsOfTiers
  account: BlockedByFeatureType | IAccount | null;
  // end of use for Features.TrialsOfTiers
}

interface DispatchProps {
  getAccountUsageForUser: (payload: IAccountUsagePayload) => void;
  fetchAccountsList: (params: ListParams<Ifilter[]>) => void;
}

interface RouteState {
  refreshAccountData: boolean;
}

type Props = RouteComponentProps<Record<string, string>, StaticContext, RouteState> & StateProps & DispatchProps;

class SubscriptionBase extends React.Component<Props> {
  componentDidMount() {
    const { accessType, fetchAccountsList, accountId } = this.props;

    if (this.props.location?.state?.refreshAccountData && accountId !== BlockedByFeature) {
      fetchAccountsList({
        filters: [
          {
            field: '_id',
            operands: [accountId],
            operator: FilterOperators.equals,
            validateInputs: false
          }
        ]
      });
    }

    if (accessType && accessType !== AccessTypes.Enterprise) {
      this.props.getAccountUsageForUser({ accessType });
    }
  }

  render() {
    const { accessType, cannotAccessThisPage, account } = this.props;
    const isUpgradeToPro = window.location.pathname.split('/').pop() === 'upgrade-to-pro';
    if (!account) {
      return null;
    }
    if (isUpgradeToPro && accessType !== AccessTypes.Lite) {
      return <Redirect to="/settings/subscription" />;
    }

    if (cannotAccessThisPage) {
      return <Redirect to="/settings/profile" />;
    }
    switch (accessType) {
    case AccessTypes.Enterprise:
      return <EnterpriseSubscription />;
    case AccessTypes.Professional:
      return <ProfessionalSubscription />;
    case AccessTypes.Lite:
      return <LiteSubscription />;
    default:
      return null;
    }
  }
}

const mapStateToProps = (state: RootState): StateProps => {
  const loggedUser = authSelectors.getLoggedInUser(state);
  const isEnterpriseAccount = accountListSelectors.isEnterpriseAccount(state, loggedUser?.accountId ?? '');
  const account = accountListSelectors.getAccountById(state, loggedUser?.accountId ?? '');
  const isUserAdmin = authSelectors.isAuthUserAdminOrOwner(state);
  const isUserSxAdmin = authSelectors.isAuthUserSightxAdmin(state);
  const cannotAccessThisPage = isTrialOfTiersEnabled ?
    loggedUser?.userType === UserTypes.User && isEnterpriseAccount : isEnterpriseAccount && !isUserAdmin && !isUserSxAdmin;

  return {
    account: notAvailablePropExceptIn({ availableIn: [Features.TrialsOfTiers], expected: account }),
    accessType: authSelectors.getAuthAccountAccessType(state),
    cannotAccessThisPage,
    accountId: loggedUser?.accountId
  };
};

const ConnectedSubscription = connect(mapStateToProps, { getAccountUsageForUser, fetchAccountsList })(SubscriptionBase);
export const Subscription = withRouter(ConnectedSubscription);
